import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTextingStepSave } from '../../hooks/useTextingStepSave';
import { useBackupEmailAttachmentSync } from '../../hooks/useBackupEmailAttachmentSync';
import { useTextingStepState } from '../../hooks/useTextingStepState';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { TextingStepForm } from '../../components/TextingStepForm';
import { TextingStepAdvancedOptions } from '../../components/TextingStepAdvancedOptions';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { AUTOMATION_QUERY_KEYS } from '../../../../../queries/Automation/queryKeys';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { isFilesFeatureEnabled } from '../../../../../models/utils/accountUtils';

interface TextingStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function TextingStepRenderer({ nodeId, data, readOnly: readOnlyProp }: TextingStepRendererProps) {
	const {
		step: textingStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<Api.ITextingAutomationStep>(data, readOnlyProp);

	const { updateNodeData, connectionTypes } = useAutomationFlowContext();
	const userSession = useUserSession();
	const account = impersonationContext?.account ?? userSession?.account?.toJs?.() ?? {};
	const canSendFileLinks =
		isFilesFeatureEnabled(account) &&
		!!account?.features?.files?.canSendFileLinks &&
		!!userSession?.user?.userGroupPermissions?.featurePermissions?.files?.sendDownloadLink;
	const canRequestFiles =
		isFilesFeatureEnabled(account) && !!userSession?.user?.userGroupPermissions?.featurePermissions?.files?.requestFile;
	const stepType = data.stepType;
	const schedule = textingStep?.schedule || stepData?.schedule;
	const templateId = template?.id;
	const queryClient = useQueryClient();

	const effectiveReadOnlyProp = readonly ? true : readOnlyProp;

	const {
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		setShowRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
	} = useTextingStepState({
		textingStep,
		template,
		readOnlyProp: effectiveReadOnlyProp,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save, onSaveStep, saveSenderOptions } = useTextingStepSave({
		textingStep,
		stepId,
		templateId,
		skipStepContactFilter,
		autoSave,
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.ITextingAutomationStep>) => {
		if (!updateNodeData || !textingStep) return;
		updateNodeData(nodeId, {
			automationStep: (_currentStep: Api.IAutomationStep) => updatedFields,
		});
	};

	const handleBackupEmailSyncSuccess = useCallback(
		(updatedEmailStep: Api.IEmailAutomationStep) => {
			syncStepToNodeData({
				conditionalSteps: {
					...textingStep?.conditionalSteps,
					[Api.AutomationStepOutcome.Error]: updatedEmailStep,
				},
			});
		},
		[syncStepToNodeData, textingStep?.conditionalSteps]
	);

	const { syncToBackupEmail } = useBackupEmailAttachmentSync({
		textingStep,
		templateId,
		impersonationContext,
		onSyncSuccess: handleBackupEmailSyncSuccess,
	});

	// Wrap onSaveStep to also sync partial step data to the canvas node
	const handleSaveStep = async (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => {
		await onSaveStep(stepToSave, newSchedule);
		if (stepToSave) {
			// Exclude sendAttachmentsAsLink from the canvas sync — this field is owned
			// by handleAttachmentStepChanged. A blur-save completing async can carry a
			// stale true value that would overwrite a concurrent toggle-off update.
			const { sendAttachmentsAsLink: _omit, ...fieldsToSync } = stepToSave;
			syncStepToNodeData(fieldsToSync);
		}
	};

	const handleSaveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		saveSenderOptions(option, sendToSender);
		syncStepToNodeData({ sendFromOptions: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const handleAttachmentStepChanged = useCallback(
		(updatedStep: Api.ITextingAutomationStep) => {
			syncStepToNodeData(updatedStep);
			if (updatedStep.sendAttachmentsAsLink && !textingStep?.sendAttachmentsAsLink) {
				// onInsertMergeField (called next) only updates the editor — it does not trigger
				// TinyMCE onChange, so this is the only save that fires for the check action.
				void save({ newSendAttachmentsAsLink: true });
			}
			syncToBackupEmail(updatedStep);
		},
		[syncStepToNodeData, save, textingStep?.sendAttachmentsAsLink, syncToBackupEmail]
	);

	// Refresh template data after step changes that may affect server-derived editor state.
	const handleStepChanged = useCallback(() => {
		if (templateId) {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
		}
	}, [queryClient, templateId]);

	const handleUpdateStep = useCallback(
		(_stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => {
			if (!updateNodeData || !textingStep) return;
			const updated = updater(textingStep as Api.IAutomationStep);
			updateNodeData(nodeId, { automationStep: updated });
		},
		[updateNodeData, nodeId, textingStep]
	);

	if (!textingStep) {
		return <div>Loading step data… Try collapsing and re-expanding this node.</div>;
	}

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
			/>
			{textingStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={textingStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
					impersonationContext={impersonationContext}
				/>
			)}
			<TextingStepForm
				step={textingStep}
				trigger={trigger}
				schedule={schedule}
				readonly={readonly}
				showRequiredFieldError={showRequiredFieldError}
				onSaveStep={handleSaveStep}
				onClearError={() => setShowRequiredFieldError(false)}
				templateId={templateId}
				stepId={stepId}
				impersonationContext={impersonationContext}
				canSendFileLinks={canSendFileLinks}
				canRequestFiles={canRequestFiles}
				onAttachmentStepChanged={handleAttachmentStepChanged}
				onStepChanged={handleStepChanged}
				updateStep={handleUpdateStep}
			/>
			<TextingStepAdvancedOptions
				step={textingStep}
				stepId={stepId || nodeId}
				nodeId={nodeId}
				filter={skipStepContactFilter}
				canEdit={canEdit}
				isExpanded={advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				onSaveSenderOptions={handleSaveSenderOptions}
				onFilterChange={handleFilterChange}
				connectionTypes={connectionTypes}
				impersonationContext={impersonationContext}
			/>
		</>
	);
}
